import { useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import { useNavigate } from "react-router";
import { useData } from "../contexts/DataContext/DataContextt";
import { usePreload } from "../contexts/DataContext/PreloadContext";
import { usePagedQuery } from "../contexts/hooks/usePagedQuery";
import PageContainer from "../components/Forms/PageContainer";
import TabulatedGridCollapsibleV2 from "../components/Forms/TabulatedGridCollapsibleV2";
import { deletUnit, fetchUnits } from "../services/unitService";
import { unitsPageCache } from "./cache/PageCache";
import { RowActions } from "../utils/RowActions";

export default function UnitPageNew() {
  const { language } = useData();
  const { confirmDialog, showNotification, openImageDialog } = usePreload();
  const navigate = useNavigate();
  const {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    handleKeyDown,
    handleSearch,
    data,
    total,
    searchTerm,
    isLoading,
    error,
    reload,
    searchType,
    setSearchType,
    searchOption,
  } = usePagedQuery({
    fetchFunction: fetchUnits,
    language,
    cache: unitsPageCache,
    captureErrors: true,
    searchOption: [
      { id: "unitCode", display: "Code" },
      { id: "unitName", display: "Name" },
    ],
  });
  const edit = useCallback(
    (row) => navigate(`/unit/edit/${row.unitCode}`, { state: { row } }),
    [navigate],
  );
  const deleteUnit = async (row) => {
    const confirmed = await confirmDialog.show({
      title: "Delete Unit",
      message: `Are you sure you want to delete unit ${row.unitCode}?`,
    });
    if (!confirmed) return;
    try {
      confirmDialog.setLoading(true);
      const result = await deletUnit(row);
      if (result instanceof Response && !result.ok)
        throw new Error(`Delete failed (${result.status})`);
      unitsPageCache.clear();
      if (page > 0 && data.length === 1) {
        handleChangePage(null, page - 1);
      } else {
        await reload(true);
      }
      showNotification("Deleted successfully", "success");
    } catch (err) {
      console.error(err);
      showNotification("Delete failed", "error");
    } finally {
      confirmDialog.setLoading(false);
      confirmDialog.close();
    }
  };
  const headers = [
    { prop: "unitCode", text: "Code", width: 130, link: { action: edit } },
    { prop: "unitAbbreviation", text: "Abbreviation", width: 130 },
    { prop: "unitName", text: "Name" },
    { prop: "unitDisplayName", text: "Display Name" },
    { prop: "unitTooltip", text: "Tooltip" },
    {
      prop: "unitImage",
      text: "Symbols",
      width: 100,
      align: "center",
      format: (value) =>
        value ? (
          <Box
            component="button"
            type="button"
            aria-label="Preview unit symbol"
            onClick={() => openImageDialog(value)}
            sx={{
              p: 0,
              border: 0,
              bgcolor: "transparent",
              cursor: "pointer",
              borderRadius: 1,
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: 2,
              },
            }}
          >
            <img src={value} alt="" width={24} height={24} loading="lazy" />
          </Box>
        ) : (
          "—"
        ),
    },
  ];
  const actions = [
    { tooltip: "Edit Unit", icon: <EditNoteRoundedIcon />, onClick: edit },
    {
      tooltip: "Delete Unit",
      icon: <DeleteForeverRoundedIcon />,
      color: "error",
      onClick: deleteUnit,
    },
  ];
  return (
    <PageContainer
      title="ETIM Units"
      subtitle="Manage ETIM units, names and symbols"
      titleAdornment={
        <Chip
          size="small"
          label={total}
          aria-label={`${total} ${searchTerm ? "matching " : ""}units`}
        />
      }
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Refresh ETIM units">
            <span>
              <IconButton
                size="small"
                aria-label="Refresh units"
                onClick={() => reload(true)}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/units/new")}
          >
            Create
          </Button>
        </Stack>
      }
    >
      <Box mt={3} sx={{ minWidth: 0 }}>
        <TabulatedGridCollapsibleV2
          variant="modern"
          loading={isLoading}
          emptyState={
            <Stack
              alignItems="center"
              spacing={1.5}
              sx={{ py: 8, color: "text.secondary" }}
            >
              <SearchOffRoundedIcon sx={{ fontSize: 40 }} />
              <Typography>
                {error ? "Unable to load units" : "No units found"}
              </Typography>
              {error ? (
                <Button size="small" onClick={() => reload(true)}>
                  Retry
                </Button>
              ) : searchTerm ? (
                <Button size="small" onClick={() => handleSearch("")}>
                  Clear search
                </Button>
              ) : null}
            </Stack>
          }
          pagination={{
            totalRows: total,
            page,
            rowsPerPage,
            handleChangePage,
            handleChangeRowsPerPage,
          }}
          search={{
            placeHolder: "Search units",
            searchOption,
            searchType,
            setSearchType,
            handleSearchClick: handleSearch,
            onKeyDown: handleKeyDown,
            activeTerm: searchTerm,
            onClear: () => handleSearch(""),
          }}
          data={data}
          headers={headers}
          rowActions={(row) => <RowActions row={row} actions={actions} />}
        />
      </Box>
    </PageContainer>
  );
}
